import React from "react";

export interface IGraphBaseProps {
    xAxis: {
        min: number;
        max: number;
        tickInterval?: IntervalType;
        converter?: (value: number) => string;
        title?: string;
        labelMinDistancePx?: number;
        // gridLineMinDistancePx?: number;
    },
    yAxis: {
        min: number;
        max: number;
        tickInterval?: IntervalType;
        converter?: (value: number) => string;
        title?: string;
        highlightValue?: number;
    }
}

export type DataPoint = {
    x: number;
    y: number;
};

export type GraphPadding = {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
};

type GraphThickness = {
    top: number
    right: number;
    bottom: number;
    left: number;
}

type IntervalType = number | ((value: number, includeCurrent?: boolean) => number);

interface IGraphBaseState {
}

abstract class GraphBase<TProps extends IGraphBaseProps> extends React.Component<IGraphBaseProps & TProps, IGraphBaseState> {
    private _canvas: React.RefObject<HTMLCanvasElement> = React.createRef();
    private _container: React.RefObject<HTMLDivElement> = React.createRef();
    private _ctx?: CanvasRenderingContext2D;
    private _scaleX: number = 0;
    private _scaleY: number = 0;
    private _width: number = 0;
    private _height: number = 0;
    private _style?: CSSStyleDeclaration;
    private _extraPadding: GraphPadding = { top: 0, left: 0, bottom: 0, right: 0 };
    private _timer: number = 0;

    constructor(props: Readonly<IGraphBaseProps & TProps>) {
        super(props);

        this.state = {};

        this.handleSizeChanged = this.handleSizeChanged.bind(this);
    }

    public componentDidMount() {
        if (!this._canvas.current) {
            throw new Error("Canvas doesn't exist on mount");
        }

        const ctx = this._canvas.current.getContext("2d");
        if (!ctx) {
            throw new Error("Could not get drawing context");
        }

        this._ctx = ctx;
        this._style = window.getComputedStyle(this._canvas.current);

        window.addEventListener("resize", this.handleSizeChanged);

        this.handleSizeChanged();
        this.redraw(ctx);

        // this._timer = window.setInterval(() => this.redraw(ctx), 2000);
    }

    public componentWillUnmount() {
        window.removeEventListener("resize", this.handleSizeChanged);
        window.clearInterval(this._timer);
    }

    public componentDidUpdate(prevProps: Readonly<TProps>) {
        if (!this._ctx) {
            throw new Error("Drawing context missing");
        }

        if (this.props.xAxis.max !== prevProps.xAxis.max || this.props.xAxis.min !== prevProps.xAxis.min ||
            this.props.yAxis.max !== prevProps.yAxis.max || this.props.yAxis.min !== prevProps.yAxis.min) {
            // Scale changed
            // this.computeInnerScale();
        }

        this.redraw(this._ctx);
    }

    public render() {
        return <div ref={this._container} className="graph-container">
            <canvas ref={this._canvas} />
            {this.props.children}
        </div>;
    }

    protected abstract redrawInner(ctx: CanvasRenderingContext2D): void;

    protected getScale() {
        return { x: this._scaleX, y: this._scaleY };
    }

    protected getInnerBounds() {
        const padding = this.getPadding();

        return {
            width: this._width - padding.left - padding.right,
            height: this._height - padding.top - padding.bottom,

            left: padding.left,
            right: this._width - padding.right,
            top: padding.top,
            bottom: this._height - padding.bottom
        }
    }

    protected getOuterBounds() {
        return {
            width: this._width,
            height: this._height
        };
    }

    protected xToPx(x: number) {
        const min = (this.props.xAxis && this.props.xAxis.min) || 0;
        return this.getPadding().left + Math.round((x - min) * this._scaleX);
    }

    protected yToPx(y: number) {
        const min = (this.props.yAxis && this.props.yAxis.min) || 0;
        const { height: innerHeight } = this.getInnerBounds();

        return this.getPadding().top + Math.round(innerHeight - ((y - min) * this._scaleY));
    }

    protected pointToPx(point: { x: number, y: number }) {
        const { top, left } = this.getPadding();
        const minX = (this.props.xAxis && this.props.xAxis.min) || 0;
        const minY = (this.props.yAxis && this.props.yAxis.min) || 0;

        const { height: innerHeight } = this.getInnerBounds();

        return {
            x: Math.round(left + ((point.x - minX) * this._scaleX)),
            y: Math.round(top + (innerHeight - ((point.y - minY) * this._scaleY)))
        };
    }

    protected getPadding() {
        const outerPadding = this.getStyleThickness("padding");

        return {
            top: (outerPadding.top || 0) + this._extraPadding.top!,
            left: (outerPadding.left || 0) + this._extraPadding.left!,
            right: (outerPadding.right || 0) + this._extraPadding.right!,
            bottom: (outerPadding.bottom || 0) + this._extraPadding.bottom!,
        };
    }

    protected drawBackground(ctx: CanvasRenderingContext2D) {
        const innerMargin = this.getPadding();
        const background = this.getStyle("background", "#fff");
        const innerBackground = this.getStyle("inner-background", "#fff");

        ctx.save();

        // Whole background
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, this._width, this._height);

        // Inner background
        ctx.translate(0.5, 0.5);
        ctx.beginPath();
        ctx.rect(innerMargin.left, innerMargin.top, this._width - innerMargin.left - innerMargin.right, this._height - innerMargin.top - innerMargin.bottom)
        ctx.fillStyle = innerBackground;
        ctx.fill();

        ctx.restore();
    }

    protected drawFrame(ctx: CanvasRenderingContext2D) {
        const innerMargin = this.getPadding();

        ctx.save();
        ctx.translate(0.5, 0.5);
        ctx.beginPath();
        ctx.rect(innerMargin.left, innerMargin.top, this._width - innerMargin.left - innerMargin.right, this._height - innerMargin.top - innerMargin.bottom)

        ctx.strokeStyle = this.getStyle("frame-stroke");
        ctx.lineWidth = this.getStyleNumber("frame-width", 1);
        ctx.stroke();

        ctx.restore();
    }

    protected drawGridlinesX(ctx: CanvasRenderingContext2D, interval?: IntervalType) {
        if (!interval) {
            if (!(interval = this.props.xAxis.tickInterval)) {
                return;
            }
        }

        const { top, bottom } = this.getPadding();
        const { max, labelMinDistancePx/*, gridLineMinDistancePx*/ } = this.props.xAxis;

        let start = (typeof interval === "function")
            ? this.props.xAxis.min + interval(this.props.xAxis.min, true)
            : Math.ceil(this.props.xAxis.min / interval) * interval;

        ctx.beginPath();
        let lastXPos = undefined;
        // let lastXPosLabelDrawn = undefined;
        for (let x = start; x < max; x += (typeof interval === "function" ? interval(x) : interval)) {
            const xPos = Math.round(this.xToPx(x)) + 0.5;

            /*if (labelMinDistancePx && (!lastXPosLabelDrawn || xPos - lastXPosLabelDrawn >= labelMinDistancePx)) {
                // Draw line
                lastXPosLabelDrawn = xPos;
            }
            else */
            if (labelMinDistancePx && lastXPos && xPos - lastXPos < labelMinDistancePx)
                continue; // Skip

            lastXPos = xPos;

            ctx.moveTo(xPos, top);
            ctx.lineTo(xPos, this._height - bottom);
        }

        ctx.strokeStyle = this.getStyle("grid-stroke");
        ctx.lineWidth = this.getStyleNumber("grid-width", 1);
        ctx.stroke();
    }

    protected drawGridlinesY(ctx: CanvasRenderingContext2D, interval?: IntervalType) {
        if (!interval) {
            if (!(interval = this.props.yAxis.tickInterval)) {
                return;
            }
        }

        const { left, right } = this.getPadding();
        const max = this.props.yAxis.max;

        interval = this.calculateInterval(interval);

        let start = (typeof interval === "function")
            ? this.props.yAxis.min + interval(this.props.yAxis.min, true)
            : Math.ceil(this.props.yAxis.min / interval) * interval;

        ctx.beginPath();
        for (let y = start; y < max; y += (typeof interval === "function" ? interval(y) : interval)) {
            const yPos = Math.round(this.yToPx(y)) + 0.5;
            ctx.moveTo(left, yPos);
            ctx.lineTo(this._width - right, yPos);
        }

        ctx.strokeStyle = this.getStyle("grid-stroke");
        ctx.lineWidth = this.getStyleNumber("grid-width", 1);
        ctx.stroke();

        if (this.props.yAxis.highlightValue !== undefined) {
            const highlightY = Math.round(this.yToPx(this.props.yAxis.highlightValue)) + 0.5;
            ctx.beginPath();
            ctx.moveTo(left, highlightY);
            ctx.lineTo(this._width - right, highlightY);
            ctx.lineWidth = this.getStyleNumber("grid-highlight-width", 1);
            ctx.strokeStyle = this.getStyle("grid-highlight-stroke");
            ctx.stroke();
        }
    }

    protected drawTicksX(ctx: CanvasRenderingContext2D, sides: "both" | "top" | "bottom" = "bottom", interval?: IntervalType) {
        if (!interval) {
            if (!(interval = this.props.xAxis.tickInterval)) {
                return;
            }
        }

        const { top, bottom } = this.getPadding();
        const max = this.props.xAxis.max;
        const tickSize = this.getStyleNumber("tick-radius", 3);
        const tickMinDistance = this.props.xAxis.labelMinDistancePx;

        let start = (typeof interval === "function")
            ? this.props.xAxis.min + interval(this.props.xAxis.min, true)
            : Math.ceil(this.props.xAxis.min / interval) * interval;

        ctx.beginPath();
        let lastXPos = undefined;
        for (let x = start; x <= max; x += (typeof interval === "function" ? interval(x) : interval)) {
            const xPos = Math.round(this.xToPx(x)) + 0.5;

            // Don't draw too densly
            if (tickMinDistance && lastXPos && xPos - lastXPos < tickMinDistance)
                continue;

            lastXPos = xPos;

            if (sides === "both" || sides === "bottom") {
                ctx.moveTo(xPos, this._height - bottom - tickSize);
                ctx.lineTo(xPos, this._height - bottom + tickSize + 1);
            }
            if (sides === "both" || sides === "top") {
                ctx.moveTo(xPos, top - tickSize);
                ctx.lineTo(xPos, top + tickSize + 1);
            }
        }

        ctx.strokeStyle = this.getStyle("tick-stroke");
        ctx.lineWidth = this.getStyleNumber("tick-width", 1);
        ctx.stroke();
    }

    protected drawTicksY(ctx: CanvasRenderingContext2D, sides: "both" | "left" | "right" = "both", interval?: IntervalType) {
        if (!interval) {
            if (!(interval = this.props.yAxis.tickInterval)) {
                return;
            }
        }

        const { left, right } = this.getPadding();
        const max = this.props.yAxis.max;
        const tickSize = this.getStyleNumber("tick-radius", 3);

        interval = this.calculateInterval(interval);

        let start = (typeof interval === "function")
            ? this.props.yAxis.min + interval(this.props.yAxis.min, true)
            : Math.ceil(this.props.yAxis.min / interval) * interval;

        ctx.beginPath();
        for (let y = start; y <= max; y += (typeof interval === "function" ? interval(y) : interval)) {
            const yPos = Math.round(this.yToPx(y)) + 0.5;
            if (sides === "both" || sides === "left") {
                ctx.moveTo(left - tickSize, yPos);
                ctx.lineTo(left + tickSize + 1, yPos);
            }
            if (sides === "both" || sides === "right") {
                ctx.moveTo(this._width - right - tickSize, yPos);
                ctx.lineTo(this._width - right + tickSize + 1, yPos);
            }
        }

        ctx.strokeStyle = this.getStyle("tick-stroke");
        ctx.lineWidth = this.getStyleNumber("tick-width", 1);
        ctx.stroke();
    }

    protected drawTickLabelsX(ctx: CanvasRenderingContext2D, sides: "both" | "top" | "bottom" = "both", interval?: IntervalType) {
        if (!interval) {
            if (!(interval = this.props.xAxis.tickInterval)) {
                return;
            }
        }

        const { top, bottom } = this.getPadding();
        const { bottom: bottomMargin, top: topMargin } = this.getStyleThickness("inner-margin");
        const max = this.props.xAxis.max;
        const tickSize = this.getStyleNumber("tick-radius", 3);
        const tickMinDistance = this.props.xAxis.labelMinDistancePx;
        const converter = (this.props.xAxis && this.props.xAxis.converter) || ((x) => x.toString());

        let start = (typeof interval === "function")
            ? this.props.xAxis.min + interval(this.props.xAxis.min, true)
            : Math.ceil(this.props.xAxis.min / interval) * interval;

        ctx.beginPath();
        ctx.textBaseline = "hanging";
        ctx.fillStyle = this.getStyle("tick-label-color", "#000");
        ctx.font = this.getStyle("tick-label-font", "arial 0.9em");
        ctx.textAlign = "center";

        let lastXPos = undefined;
        for (let x = start; x <= max; x += (typeof interval === "function" ? interval(x) : interval)) {
            const xPos = Math.round(this.xToPx(x)) + 0.5;
            const text = converter(x);

            // Don't draw too densly
            if (tickMinDistance && lastXPos && xPos - lastXPos < tickMinDistance)
                continue;

            lastXPos = xPos;

            if (sides === "both" || sides === "bottom") {
                ctx.fillText(text, xPos, this._height - bottom + tickSize + bottomMargin);
            }
            if (sides === "both" || sides === "top") {
                ctx.fillText(text, xPos, top - tickSize - topMargin, xPos);
            }
        }

        ctx.strokeStyle = this.getStyle("tick-stroke");
        ctx.lineWidth = this.getStyleNumber("tick-width", 1);
        ctx.stroke();
    }

    protected drawTickLabelsY(ctx: CanvasRenderingContext2D, sides: "both" | "left" | "right" = "both", interval?: IntervalType) {
        if (!interval) {
            if (!(interval = this.props.yAxis.tickInterval)) {
                return;
            }
        }

        const { left, right } = this.getPadding();
        const { left: leftMargin, right: rightMargin } = this.getStyleThickness("inner-margin");
        const max = this.props.yAxis.max;
        const tickSize = this.getStyleNumber("tick-radius", 3);
        const converter = (this.props.yAxis && this.props.yAxis.converter) || ((v) => v.toString());

        interval = this.calculateInterval(interval);

        let start = (typeof interval === "function")
            ? this.props.yAxis.min + interval(this.props.yAxis.min, true)
            : Math.ceil(this.props.yAxis.min / interval) * interval;

        ctx.beginPath();
        ctx.textBaseline = "middle";
        ctx.fillStyle = this.getStyle("tick-label-color", "#000");
        ctx.font = this.getStyle("tick-label-font", "arial 0.9em");

        for (let y = start; y <= max; y += (typeof interval === "function" ? interval(y) : interval)) {
            const yPos = Math.round(this.yToPx(y)) + 0.5;
            const text = converter(y);

            if (sides === "both" || sides === "left") {
                ctx.textAlign = "right";
                ctx.fillText(text, left - tickSize - leftMargin, yPos);
            }
            if (sides === "both" || sides === "right") {
                ctx.textAlign = "left";
                ctx.fillText(text, (this._width - right + tickSize + 1 + rightMargin), yPos);
            }
        }

        ctx.strokeStyle = this.getStyle("tick-stroke");
        ctx.lineWidth = this.getStyleNumber("tick-width", 1);
        ctx.stroke();
    }

    protected drawAxisTitles(ctx: CanvasRenderingContext2D) {
        const xOffset = this.getStyleNumber("axis-label-x-offset", 0);
        const yOffset = this.getStyleNumber("axis-label-y-offset", 0);

        const { width: innerWidth, height: innerHeight, left: innerLeft, top: innerTop } = this.getInnerBounds();

        ctx.save();
        ctx.font = this.getStyle("axis-label-font", "1em Arial");
        ctx.fillStyle = this.getStyle("axis-label-color", "#000");
        ctx.textAlign = "center";

        if (this.props.xAxis.title) {
            ctx.textBaseline = "bottom";
            ctx.font = this.getStyle("axis-label-x-font", ctx.font);
            ctx.fillText(this.props.xAxis.title, Math.round((innerWidth / 2) + innerLeft), this._height - xOffset);
        }

        if (this.props.yAxis.title) {
            ctx.font = this.getStyle("axis-label-y-font", ctx.font);
            ctx.textBaseline = "hanging";
            ctx.save();
            ctx.translate(yOffset + 0.5, Math.round((innerHeight / 2) + innerTop));
            ctx.rotate(-1.5708); // -90 deg in rads
            ctx.fillText(this.props.yAxis.title, 0, 0);
            ctx.restore();
        }

        ctx.restore();
    }

    protected clipInner(ctx: CanvasRenderingContext2D) {
        const padding = this.getPadding();

        ctx.save();
        ctx.beginPath();
        ctx.rect(padding.left, padding.top, this._width - padding.left - padding.right, this._height - padding.top - padding.bottom);
        ctx.clip();
    }

    protected getStyle(property: string, defaultValue?: string): string {
        if (!this._style) {
            return "";
        }

        return this._style.getPropertyValue(`--graph-${property}`).trim() || defaultValue || "";
    }

    protected getStyleNumber(property: string, defaultValue?: number): number {
        const value = this.getStyle(property, "");
        if (value === "") {
            return defaultValue || 0;
        }

        return parseFloat(value);
    }

    protected getStyleThickness(property: string, defaultValue?: GraphThickness | string): GraphThickness {
        let value = this.getStyle(property, "");
        if (value === "") {
            if (typeof defaultValue === "object") {
                return defaultValue;
            }
            else if (!defaultValue) {
                return { top: 0, left: 0, right: 0, bottom: 0 };
            }

            value = defaultValue;
        }

        const parts = value.split(" ");
        if (parts.length === 1) {
            const value1 = parseFloat(parts[0]) || 0;
            return { top: value1, bottom: value1, left: value1, right: value1 };
        }
        else if (parts.length === 2) {
            const valueUD = parseFloat(parts[0]) || 0;
            const valueLR = parseFloat(parts[1]) || 0;
            return { top: valueUD, bottom: valueUD, left: valueLR, right: valueLR };
        }
        else if (parts.length === 3) {
            const valueLR = parseFloat(parts[1]) || 0;
            return {
                top: parseFloat(parts[0]) || 0,
                right: valueLR,
                bottom: parseFloat(parts[2]) || 0,
                left: valueLR,
            };
        }
        else if (parts.length >= 4) {
            return {
                top: parseFloat(parts[0]) || 0,
                right: parseFloat(parts[1]) || 0,
                bottom: parseFloat(parts[2]) || 0,
                left: parseFloat(parts[3]) || 0
            };
        }

        return { top: 0, left: 0, right: 0, bottom: 0 };
    }

    protected setExtraPadding(padding: GraphPadding, mode: "add" | "set" = "add") {
        if (!padding) {
            return;
        }

        if (typeof padding.top !== "undefined")
            this._extraPadding.top = mode === "set" ? padding.top : (this._extraPadding.top || 0) + padding.top;
        if (typeof padding.left !== "undefined")
            this._extraPadding.left = mode === "set" ? padding.left : (this._extraPadding.left || 0) + padding.left;
        if (typeof padding.bottom !== "undefined")
            this._extraPadding.bottom = mode === "set" ? padding.bottom : (this._extraPadding.bottom || 0) + padding.bottom;
        if (typeof padding.right !== "undefined")
            this._extraPadding.right = mode === "set" ? padding.right : (this._extraPadding.right || 0) + padding.right;

        this.computeInnerScale();
    }

    private redraw(ctx: CanvasRenderingContext2D) {
        if (this._canvas.current && this._container.current && (this._container.current.clientWidth !== this._width || this._container.current.clientHeight !== this._height)) {
            this.resetSize(this._canvas.current, this._container.current)
        }

        this.redrawInner(ctx);
    }

    private handleSizeChanged(e?: UIEvent) {
        const container = this._container.current;
        const canvas = this._canvas.current;
        if (!container || !canvas || !this._ctx) {
            return;
        }

        this.resetSize(canvas, container);

        // TODO: Determine if this needs debouncing

        this.redraw(this._ctx);
    }

    private resetSize(canvas: HTMLCanvasElement, container: HTMLDivElement) {
        // Stash container's width so everything that needs the "width" can get the proper value when
        // on a high DPI display where the canvas is scaled
        this._width = container.clientWidth;
        this._height = container.clientHeight;

        canvas.width = container.clientWidth * window.devicePixelRatio;
        canvas.height = container.clientHeight * window.devicePixelRatio;
        if (this._ctx) {
            this._ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        }

        // console.log(`Resize ${container.clientWidth} x ${container.clientHeight}`);
    }

    private computeInnerScale() {
        const container = this._container.current;
        if (!container) {
            return;
        }

        const padding = this.getPadding();
        const innerWidth = container.clientWidth - padding.left - padding.right;
        const innerHeight = container.clientHeight - padding.top - padding.bottom;

        this.computeScale(innerWidth, innerHeight);
    }

    private computeScale(width: number, height: number) {
        let scaleX = 1;
        let scaleY = 1;

        if (this.props.xAxis) {
            scaleX = width / (this.props.xAxis.max - this.props.xAxis.min);
        }
        if (this.props.yAxis) {
            scaleY = height / (this.props.yAxis.max - this.props.yAxis.min);
        }

        this._scaleX = scaleX;
        this._scaleY = scaleY;

        // console.log(`Scale: X: ${scaleX} Y: ${scaleY}`);
    }

    private calculateInterval(interval: IntervalType) {
        // Attempt to find best interval for scale lines
        let usedInterval = interval;
        if (typeof usedInterval !== "function") {
            while (Math.abs(usedInterval * this._scaleY) < 20) {
                usedInterval += usedInterval as number;
            }
        }

        return usedInterval;
    }
}

export default GraphBase;
